import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AppData, Role } from "@/types";
import { useAuth } from "@/store/AuthStore";
import { loadAll, subscribeNotifications } from "@/store/api";
import type { AppContextValue, StoreCtx } from "@/store/appTypes";
import { ROLE_KEY } from "@/store/appTypes";
import { createEmptyData, readPeriod } from "@/store/appDefaults";
import { useCoreActions } from "@/store/hooks/useCoreActions";
import { useCatalogActions } from "@/store/hooks/useCatalogActions";
import { useClientActions } from "@/store/hooks/useClientActions";
import { useSaleActions } from "@/store/hooks/useSaleActions";
import { useAdvisorOpsActions } from "@/store/hooks/useAdvisorOpsActions";
import { useFulfillmentActions } from "@/store/hooks/useFulfillmentActions";
import { useFundActions } from "@/store/hooks/useFundActions";
import { useFinanceActions } from "@/store/hooks/useFinanceActions";
import { useContentActions } from "@/store/hooks/useContentActions";
import { useBackupActions } from "@/store/hooks/useBackupActions";

// Re-exporto los tipos de entrada/resultado para que los consumidores sigan
// importándolos desde "@/store/AppStore" sin cambios.
export type {
  AdvisorDeliveryInput,
  PurchaseToStockInput,
  RestockToAdvisorInput,
  EncargoDeliveryInput,
} from "@/store/appTypes";

const AppContext = createContext<AppContextValue | null>(null);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const [data, setData] = useState<AppData>(() => {
    const base = createEmptyData();
    base.period = readPeriod();
    return base;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roleForProfile = useCallback((): Role => {
    const stored = localStorage.getItem(ROLE_KEY);
    if (profile?.role === "admin" && (stored === "asesor" || stored === "admin")) {
      return stored;
    }
    return profile?.role === "admin" ? "admin" : "asesor";
  }, [profile]);

  const refresh = useCallback(async () => {
    if (!user) {
      setData((p) => ({ ...createEmptyData(), period: p.period, role: p.role }));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const loaded = await loadAll();
      setData((prev) => ({ ...prev, ...loaded, role: roleForProfile() }));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, [user, roleForProfile]);

  useEffect(() => {
    if (authLoading) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, profile?.role]);

  // Notificaciones en tiempo real (nuevas alertas aparecen al instante).
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeNotifications((n) => {
      setData((p) =>
        p.notifications.some((x) => x.id === n.id)
          ? p
          : { ...p, notifications: [n, ...p.notifications] }
      );
    });
    return unsubscribe;
  }, [user]);

  const run = useCallback(async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Ocurrió un error al guardar.");
    }
  }, []);

  const ctx: StoreCtx = { data, setData, setError, run, refresh, user, refreshProfile };

  const core = useCoreActions(ctx);
  const catalog = useCatalogActions(ctx);
  const clients = useClientActions(ctx);
  const sales = useSaleActions(ctx);
  const advisorOps = useAdvisorOpsActions(ctx);
  const fulfillment = useFulfillmentActions(ctx);
  const funds = useFundActions(ctx);
  const finance = useFinanceActions(ctx);
  const content = useContentActions(ctx);
  const backup = useBackupActions(ctx, core.updateSettings);

  const value = useMemo<AppContextValue>(
    () => ({
      data,
      loading,
      error,
      refresh,
      setRole: core.setRole,
      setPeriod: core.setPeriod,
      upsertProduct: catalog.upsertProduct,
      deleteProduct: catalog.deleteProduct,
      addDistributor: advisorOps.addDistributor,
      updateDistributor: advisorOps.updateDistributor,
      setApprovalStatus: advisorOps.setApprovalStatus,
      deleteDistributor: advisorOps.deleteDistributor,
      addClient: clients.addClient,
      updateClient: clients.updateClient,
      deleteClient: clients.deleteClient,
      deleteClients: clients.deleteClients,
      addSale: sales.addSale,
      setInventory: advisorOps.setInventory,
      applyInventoryAdjustment: advisorOps.applyInventoryAdjustment,
      transferInventoryStock: advisorOps.transferInventoryStock,
      addAdvisorMovement: advisorOps.addAdvisorMovement,
      deleteAdvisorMovement: advisorOps.deleteAdvisorMovement,
      deliverToAdvisor: fulfillment.deliverToAdvisor,
      addPurchaseToStock: fulfillment.addPurchaseToStock,
      restockToAdvisor: fulfillment.restockToAdvisor,
      registerAdvisorSale: sales.registerAdvisorSale,
      addExpense: funds.addExpense,
      updateExpense: funds.updateExpense,
      deleteExpense: funds.deleteExpense,
      addFundTransfer: funds.addFundTransfer,
      removeFundMovement: funds.removeFundMovement,
      payExpenseFromFund: funds.payExpenseFromFund,
      payExpenseWithLoan: funds.payExpenseWithLoan,
      addReceivable: finance.addReceivable,
      updateReceivable: finance.updateReceivable,
      removeReceivable: finance.removeReceivable,
      addLoan: finance.addLoan,
      removeLoan: finance.removeLoan,
      addPromotion: content.addPromotion,
      deletePromotion: content.deletePromotion,
      addEncargo: content.addEncargo,
      setEncargoStatus: content.setEncargoStatus,
      deliverEncargo: fulfillment.deliverEncargo,
      deleteEncargo: content.deleteEncargo,
      addRestockItem: content.addRestockItem,
      deleteRestockItem: content.deleteRestockItem,
      pushNotification: core.pushNotification,
      markNotificationRead: core.markNotificationRead,
      markAllRead: core.markAllRead,
      updateSettings: core.updateSettings,
      updateProfileContact: core.updateProfileContact,
      exportBackup: backup.exportBackup,
      restoreBackup: backup.restoreBackup,
      importCatalogProducts: catalog.importCatalogProducts,
      loadDemo: backup.loadDemo,
      clearAll: backup.clearAll,
      deleteMany: backup.deleteMany,
    }),
    [
      data,
      loading,
      error,
      refresh,
      core,
      catalog,
      clients,
      sales,
      advisorOps,
      fulfillment,
      funds,
      finance,
      content,
      backup,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextValue => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp debe usarse dentro de AppProvider");
  return ctx;
};